import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { axiosInstance } from 'src/utils/axios/axios-instance';
import type {
  AvailablePlansResponse,
  BillingBillDetailResponse,
  BillingBillsResponse,
  CancelSubscriptionRequest,
  CurrentPlanResponse,
  DowngradePlanRequest,
  OwnerPlanProfileResponse,
  PaymentOtpRequest,
  PaymentOtpResponse,
  PaymentTermsRequest,
  PaymentTermsResponse,
  ReactivateSubscriptionRequest,
  SubscriptionChangeResponse,
  SubscriptionUpgradePreviewRequest,
  SubscriptionUpgradePreviewResponse,
  UpgradePlanRequest,
} from 'src/state/api-models/plan-subscription';
import { BILLING_API_KEY, BILLING_API_URL } from 'src/config';
import { getStoreId, getTokenValue } from 'src/state/ducks/common/auth/storage';

const OWNER_PLAN_CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours
const OWNER_PLAN_STORAGE_KEY = 'espa247_owner_plan_profile_cache_v1';

type OwnerPlanCacheEntry = {
  timestamp: number;
  response: AxiosResponse<OwnerPlanProfileResponse>;
};

let ownerPlanProfileCache: OwnerPlanCacheEntry | null = null;
let ownerPlanProfileRequest: Promise<AxiosResponse<OwnerPlanProfileResponse>> | null = null;

export const clearOwnerPlanProfileCache = () => {
  ownerPlanProfileCache = null;
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const storeId = getStoreId();
      if (!storeId) {
        return;
      }
      window.localStorage.removeItem(`${OWNER_PLAN_STORAGE_KEY}_${storeId}`);
    } catch (_err) {
      // no-op
    }
  }
};

type GetOwnerPlanProfileOptions = {
  forceRefresh?: boolean;
  storeId?: string;
};

const getBillingRequestConfig = (overrideConfig?: AxiosRequestConfig & { storeId?: string }) => {
  const headers: Record<string, string> = {};

  if (BILLING_API_KEY) {
    headers['x-api-key'] = BILLING_API_KEY;
  }

  // For billing APIs, use storeId as Bearer token (not POS token)
  // Priority: storeId from parameter > storeId from storage
  const storeId = overrideConfig?.storeId || getStoreId();
  if (storeId) {
    headers.Authorization = `Bearer ${storeId}`;
  } else {
    // Fallback: If no storeId, try using POS token (for backward compatibility)
    const token = getTokenValue();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  // Remove storeId from config before returning (it's not a valid axios config property)
  const { storeId: _, ...restConfig } = overrideConfig || {};

  return {
    baseURL: BILLING_API_URL,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    timeout: 20000, // 20 seconds timeout
    ...restConfig,
  };
};

export const getOwnerPlanProfile = (options?: GetOwnerPlanProfileOptions) => {
  if (!ownerPlanProfileCache) {
    ownerPlanProfileCache = getStoredOwnerPlanProfile();
  }

  const isCacheValid = !!ownerPlanProfileCache && Date.now() - ownerPlanProfileCache.timestamp <= OWNER_PLAN_CACHE_TTL;

  if (!options?.forceRefresh && isCacheValid && ownerPlanProfileCache) {
    return Promise.resolve(ownerPlanProfileCache.response);
  }

  if (!options?.forceRefresh && ownerPlanProfileRequest) {
    return ownerPlanProfileRequest;
  }

  // Delay 1 second before calling API
  ownerPlanProfileRequest = new Promise<AxiosResponse<OwnerPlanProfileResponse>>((resolve) => {
    setTimeout(() => {
      axiosInstance
        .get<OwnerPlanProfileResponse>('/billing/owner/profile', getBillingRequestConfig({ storeId: options?.storeId }))
        .then((response) => {
          const timestamp = Date.now();
          ownerPlanProfileCache = { timestamp, response };
          persistOwnerPlanProfile(response, timestamp);
          resolve(response);
        })
        .catch((error) => {
          ownerPlanProfileRequest = null;
          
          // Log SSL certificate errors for debugging
          const isSSLError = 
            error?.code === 'ERR_CERT_AUTHORITY_INVALID' ||
            error?.code === 'ERR_CERT_COMMON_NAME_INVALID' ||
            error?.code === 'CERT_HAS_EXPIRED' ||
            error?.message?.includes('certificate') ||
            error?.message?.includes('SSL') ||
            error?.message?.includes('Network Error');
          
          if (isSSLError) {
            console.error('[Billing API] SSL Certificate Error:', {
              message: error?.message,
              code: error?.code,
              url: error?.config?.baseURL || error?.config?.url,
              suggestion: 'Check SSL certificate configuration on the server or use cached data',
            });
          }
          
          throw error;
        })
        .finally(() => {
          ownerPlanProfileRequest = null;
        });
    }, 1000);
  });

  return ownerPlanProfileRequest;
};

export const getAvailablePlans = () => {
  return axiosInstance.get<AvailablePlansResponse>(
    '/billing/plans',
    getBillingRequestConfig({
      params: { product_type: 8 },
    })
  );
};

export const upgradePlan = (payload: UpgradePlanRequest) => {
  return axiosInstance.post<SubscriptionChangeResponse>(
    '/billing/subscription/upgrade',
    payload,
    getBillingRequestConfig()
  );
};

export const downgradePlan = (payload: DowngradePlanRequest) => {
  return axiosInstance.post<SubscriptionChangeResponse>(
    '/billing/subscription/downgrade',
    payload,
    getBillingRequestConfig()
  );
};

export const cancelSubscription = (payload: CancelSubscriptionRequest) => {
  return axiosInstance.post<SubscriptionChangeResponse>(
    '/billing/subscription/cancel',
    payload,
    getBillingRequestConfig()
  );
};

export const getSubscriptionUpgradePreview = (payload: SubscriptionUpgradePreviewRequest, storeId?: string) => {
  return axiosInstance.post<SubscriptionUpgradePreviewResponse>(
    '/billing/subscription/upgrade/preview',
    payload,
    getBillingRequestConfig({ storeId })
  );
};

export const requestPaymentOtp = (payload: PaymentOtpRequest, storeId?: string) => {
  return axiosInstance.post<PaymentOtpResponse>(
    '/billing/payment/otp/request',
    payload,
    getBillingRequestConfig({ storeId })
  );
};

const TERMS_CACHE_TTL = 12 * 60 * 60 * 1000;
const paymentTermsCache: Record<string, { timestamp: number; response: AxiosResponse<PaymentTermsResponse> }> = {};

export const getPaymentTerms = (payload?: PaymentTermsRequest) => {
  const billingCycle = payload?.billing_cycle ?? '1m';
  const cached = paymentTermsCache[billingCycle];

  if (cached && Date.now() - cached.timestamp < TERMS_CACHE_TTL) {
    return Promise.resolve(cached.response);
  }

  return axiosInstance
    .get<PaymentTermsResponse>(
      '/billing/payment/terms',
      getBillingRequestConfig({
        params: payload,
      })
    )
    .then((response) => {
      paymentTermsCache[billingCycle] = {
        timestamp: Date.now(),
        response,
      };
      return response;
    });
};

export const reactivateSubscription = (payload: ReactivateSubscriptionRequest, storeId?: string) => {
  return axiosInstance.post<SubscriptionChangeResponse>(
    '/billing/subscription/renew',
    payload,
    getBillingRequestConfig({ storeId })
  );
};

export const getBillingBills = (params?: { page?: number }) => {
  return axiosInstance.get<BillingBillsResponse>(
    '/billing/bills',
    getBillingRequestConfig({
      params,
    })
  );
};

export const getBillingBillDetail = (billId: string) => {
  return axiosInstance.get<BillingBillDetailResponse>(`/billing/bills/${billId}`, getBillingRequestConfig());
};

export const getCurrentPlan = (storeId?: string) => {
  return axiosInstance.get<CurrentPlanResponse>('/billing/current-plan', getBillingRequestConfig({ storeId }));
};

const getStoredOwnerPlanProfile = (): OwnerPlanCacheEntry | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const storeId = getStoreId();
    if (!storeId) {
      return null;
    }

    const storageKey = `${OWNER_PLAN_STORAGE_KEY}_${storeId}`;
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed: { timestamp: number; data: OwnerPlanProfileResponse } = JSON.parse(raw);

    if (!parsed || typeof parsed.timestamp !== 'number' || !parsed.data) {
      return null;
    }

    if (Date.now() - parsed.timestamp > OWNER_PLAN_CACHE_TTL) {
      const storeId = getStoreId();
      if (storeId) {
        window.localStorage.removeItem(`${OWNER_PLAN_STORAGE_KEY}_${storeId}`);
      }
      return null;
    }

    const response: AxiosResponse<OwnerPlanProfileResponse> = {
      data: parsed.data,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {},
    };

    return {
      timestamp: parsed.timestamp,
      response,
    };
  } catch (_err) {
    return null;
  }
};

const persistOwnerPlanProfile = (response: AxiosResponse<OwnerPlanProfileResponse>, timestamp: number) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const storeId = getStoreId();
    if (!storeId) {
      return;
    }

    const payload = JSON.stringify({
      timestamp,
      data: response.data,
    });
    const storageKey = `${OWNER_PLAN_STORAGE_KEY}_${storeId}`;
    window.localStorage.setItem(storageKey, payload);
  } catch (_err) {
    // no-op
  }
};
